import { sanitizeTitle } from './sanitizeTitle.js'

/**
 * Toc Class
 */
export class Toc {

	/**
	 * @private
	 * @type {boolean}
	 * Checks if device is in mobile mode.
	 */
	#isMobile = false;

	/**
	 * @private
	 * @type {string}
	 * The list of heading tag names (e.g., 'h2,h3,h4') to include in the table of contents.
	 */
	#headingsTags;

	/**
	 * @private
	 * @type {HTMLElement|null}
	 * Stores the collection of headings.
	 */
	#headings;

	/**
	 * @private
	 * @type {HTMLElement}
	 * The container element that holds the headings.
	 */
	#headingsContainer;

	/**
	 * @private
	 * @type {HTMLElement}
	 * The container where the table of contents will be generated.
	 */
	#tocContainer;

	/**
	 * @private
	 * @type {boolean}
	 * Flag indicating that a scroll to a heading was triggered via a TOC link click.
	 */
	#isHandleScrollToHeading = false;

	/**
	 * Constructs a Table of Contents (ToC) generator.
	 *
	 * @class
	 * @param {HTMLElement} headingsContainer - The container that holds the heading elements to be indexed.
	 * @param {HTMLElement} tocContainer - The container where the generated table of contents will be inserted.
	 * @param {string} headingsTags - A comma-separated string of heading tag names to include (e.g., 'h2, h3, h4').
	 *
	 * @throws {Error} If either `headingsContainer` or `tocContainer` is missing.
	 */
	constructor( headingsContainer, tocContainer, headingsTags ) {

		this.#headingsContainer = headingsContainer;
		this.#tocContainer = tocContainer;
		this.#headingsTags = headingsTags;

		if ( !this.#headingsContainer || !this.#tocContainer ) {

			throw new Error( 'Headings container or Toc container not found' );

		}

		this.#isMobile = window.matchMedia( `(max-width: 1023px)` ).matches;
		this.#headings = headingsContainer?.querySelectorAll( this.#headingsTags || 'h2, h3' );
		this.init();

	}

	/**
	 * Initializes table of content functionality.
	 *
	 * @return {void} Returns nothing.
	 */
	init = () => {

		this.setAttributes();
		this.createToc();
		this.handleScrollToHeading();

		if( !this.#isMobile && this.#headings.length ) {

			this.observeHeadings();

		}

	}

	/**
	 * Calculates the total height of the page header and top bar.
	 *
	 * @return {string} The combined height in pixels (e.g., "100px"). Returns "0px" if neither element is found.
	 */
	getHeaderHeight = () => {

		const headerHeight = document.querySelector( '.page-header' )?.offsetHeight;

		if ( ! headerHeight ) {

			return '';

		}

		return `${headerHeight}`;

	}

	/**
	 * Sets necessary attributes for headings and TOC wrapper based on header height.
	 *
	 * @return {void} Returns nothing.
	 */
	setAttributes = () => {

		const headerHeight = this.getHeaderHeight();

		if ( !headerHeight ) {

			return;

		}

		this.setHeadingsAttributes( headerHeight );

	}

	/**
	 * Sets unique ID attributes and scroll margin styles for each heading.
	 *
	 * @param {string} headerHeight
	 * @return {void} Returns nothing.
	 */
	setHeadingsAttributes = ( headerHeight ) => {

		if ( !this.#headings.length ) {

			return;

		}

		this.#headings.forEach( ( header ) => {

			const headingText = header.textContent;
			const headingId = sanitizeTitle( headingText );

			header.setAttribute( 'id', headingId );
			header.style.scrollMargin = `${headerHeight}px`;

		} );

	}

	/**
	 * Creates a Table of Contents (TOC) list based on the extracted headings and appends it to the specified TOC container.
	 *
	 * @return {void} Returns nothing.
	 */
	createToc = () => {

		if ( !this.#headings.length ) {

			const toc = this.#tocContainer?.closest( '.b-table-of-content' );

			if ( toc ) {

				toc.classList.add( 'b-table-of-content--hidden' );

			}

			return;

		}

		const list = document.createElement( "ul" );
		const parents = [list];
		let currentLevel = 2;
		let lastLi = null;
		list.className = 'toc-list';

		this.#headings.forEach( heading => {

			const level = parseInt( heading.tagName.slice( 1 ) );
			const a = document.createElement( 'a' );
			a.href = `#${heading.id}`;
			a.textContent = heading.textContent;
			a.className = 'toc-list__link';

			const li = document.createElement( 'li' );
			li.className = 'toc-list__element';
			li.appendChild( a );

			// Adds nested levels.
			if ( level > currentLevel ) {

				const newUl = document.createElement( 'ul' );
				newUl.className = `toc-list toc-list--lvl-${level}`;
				lastLi?.appendChild( newUl );
				parents.push( newUl );
				currentLevel = level;

			}

			// If the level is lower – return to a higher-level list.
			while ( level < currentLevel ) {

				parents.pop();
				currentLevel--;

			}

			parents[parents.length - 1].appendChild( li );
			lastLi = li;

			this.#tocContainer.appendChild( list );

		} );

	}

	/**
	 * Handles TOC link clicks by manually highlighting the clicked item and
	 * temporarily disabling automatic TOC updates from the IntersectionObserver.
	 *
	 * @return {void} Returns nothing.
	 */
	handleScrollToHeading = () => {

		this.#tocContainer.querySelectorAll( 'a[href^="#"]' ).forEach( link => {

			link.addEventListener( 'click', () => {

				this.#isHandleScrollToHeading = true;
				this.#tocContainer.querySelector( '.active-link' )?.classList.remove( 'active-link' );
				link.classList.add( 'active-link' );

				setTimeout( () => {

					this.#isHandleScrollToHeading = false;

				}, 1500 );

			} );

		} );

	}

	/**
	 * Observes the specified heading elements within a post and updates the Table of Contents (TOC) based on their visibility within the viewport.
	 *
	 * @return {void} Returns nothing.
	 */
	observeHeadings = () => {

		/**
		 * Observes elements when they are below 100px from the top of the screen and above half of the screen height.
		 */
		const observerOptions = {
			rootMargin: `-100px 0px ${ ( window.innerHeight / 2 ) * -1 }px 0px`,
			threshold: 0,
		}

		const observer = new IntersectionObserver( ( elements ) => {

			elements.forEach( el => {

				if ( this.#isHandleScrollToHeading ) {

					return;

				}

				const headingId = el.target.id;
				const tocElement = this.#tocContainer.querySelector( `a[href="#${headingId}"]` );
				const currentActiveElement = this.#tocContainer.querySelector( '.active-link' );

				if ( el.isIntersecting ) {

					// When a heading becomes visible in the observer area, activate the corresponding TOC link.
					currentActiveElement?.classList.remove( 'active-link' );
					tocElement.classList.add( 'active-link' );

				} else {

					// If currently active heading is no longer visible in the observer area.
					if ( el.target.getAttribute( 'id' ) === currentActiveElement?.getAttribute( 'href' )?.substring( 1 ) ) {

						// If the page is scrolled up.
						if ( el.boundingClientRect.top > ( window.innerHeight / 2 ) ) {

							// Activate the previous corresponding TOC link.
							const prevTocHeading = currentActiveElement?.closest( '.toc-list__element' )?.previousElementSibling;

							currentActiveElement?.classList.remove( 'active-link' );
							prevTocHeading?.querySelector( 'a' )?.classList.add( 'active-link' );

						}

					}

					// If the first or last heading goes out of the observer area
					if ( ( el.target === this.#headings[0] || el.target === this.#headings[this.#headings.length - 1] ) ) {

						tocElement.classList.remove( 'active-link' );

					}

				}

			} );

		}, observerOptions );

		this.#headings.forEach( heading => observer.observe( heading ) );

	}

}
